import { z } from "zod";
/**
 * `session_logs.format`'s check is the six the library declares, and `sessions.ts` is where
 * the library's vocabulary reaches this wire — so it is IMPORTED here rather than restated.
 * Not re-exported: the barrel already publishes it from `./sessions`, and two `export *`
 * sources offering one name is an ambiguity nobody should have to reason about.
 *
 * A log carries splits and a round count, so it records a scored CONDITIONING piece; a
 * strength format has neither, which is why the strength formats are in no union on this wire.
 */
import {
  blockIdSchema,
  cancelReasonSchema,
  cancelStageSchema,
  gearIdSchema,
  metconFormatIdSchema,
} from "./sessions";

/**
 * WHAT HAPPENED — the athlete's own record of a session they trained.
 *
 * MIRRORS THREE TABLES:
 *   `db/schemas/tables/session_logs.sql`       → `sessionLogSchema`
 *   `db/schemas/tables/session_log_splits.sql` → `sessionLogSplitSchema`
 *   `db/schemas/tables/session_favourites.sql` → `isFavourite` on the log, and the
 *                                                favourite/unfavourite actions
 *
 * ═══ RATINGS ARE 1–5, AND NOTHING CONVERTS THEM ═══
 *
 * `z.number().int().min(1).max(5)`, matching `check (x between 1 and 5)` on every rating
 * column. Ten steps is more resolution than a person has an opinion about, so the resolution
 * is not collected and therefore cannot be faked back later.
 *
 * The engine's `CompletionReport.rpe` and `LineReport.rpe` carry a doc comment reading
 * "1–10". That describes v1's scale, NOT a computation: both fields are used in exactly one
 * place — `api/src/engine/observation/residual.ts` — where each is pushed as an observation
 * with `predicted: null` and therefore `residual: null`. The engine's own comment there reads
 * "NOTHING PREDICTS AN RPE… It is a covariate." It never compares one, never thresholds one,
 * never branches on one.
 *
 * SO 1–5 IS WHAT IS TAPPED, 1–5 IS WHAT IS STORED, AND 1–5 IS WHAT IS PASSED THROUGH. There
 * is no conversion to write anywhere, and inventing a 1→10 mapping would put the stored row
 * and the engine's `CompletionRecord` on different scales inside one transaction.
 */
export const ratingSchema = z.number().int().min(1).max(5);

/** Which standard they held themselves to. `Movement.dim` declares both where a movement has one. */
export const LOG_LOADS = ["rx", "scaled"] as const;
export const logLoadSchema = z.enum(LOG_LOADS);
export type LogLoad = (typeof LOG_LOADS)[number];

/** `CompletionOutcome`. */
export const COMPLETION_OUTCOMES = ["started", "abandoned", "completed"] as const;
export const completionOutcomeSchema = z.enum(COMPLETION_OUTCOMES);
export type CompletionOutcome = (typeof COMPLETION_OUTCOMES)[number];


/**
 * WHERE EACH ROUND ENDED ON THE CLOCK — `SplitReport`, as its own row.
 *
 * `elapsedSeconds` IS ELAPSED ON THE SESSION CLOCK, not the round's own duration. The
 * difference between consecutive splits is the round; storing the difference instead would
 * make one missed split corrupt every later one.
 */
export const sessionLogSplitSchema = z.object({
  /** Round one is the first. Zero would be a split before the workout started. */
  round: z.number().int().positive(),
  elapsedSeconds: z.number().int().positive(),
});

export type SessionLogSplitDTO = z.infer<typeof sessionLogSplitSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   WHAT WAS ACTUALLY MOVED — the per-set record, which this wire did not carry
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * WHETHER A PERSON ENTERED THIS SET, OR THE SERVICE WORKED IT OUT.
 *
 * 🚨 THE TWO MUST NEVER BE ADDED TOGETHER WITHOUT A LABEL, which is the whole reason this is
 * on the wire rather than being an implementation detail of the table.
 *
 * `logged` — the athlete tapped the weight and the reps. The strength screen writes one of
 * these per set, and it is the only kind the engine is allowed to see: a `LineReport` is
 * evidence, and evidence has to have been observed.
 *
 * `derived` — nobody was asked. A metcon is not logged set by set; the athlete banks a round,
 * not a rep, so a conditioning line is the prescription multiplied by the rounds they
 * completed. For an AMRAP that is a real count of real work; for a for-time piece the clock
 * caught mid-round it OVERCOUNTS the last round, and no arithmetic can fix that because
 * nothing recorded where in the round they were. A volume chart may sum both — that IS what a
 * training volume is — and it has to be able to say how much of the number was measured.
 */
export const LINE_SOURCES = ["logged", "derived"] as const;
export const lineSourceSchema = z.enum(LINE_SOURCES);
export type LineSource = (typeof LINE_SOURCES)[number];

/**
 * WHAT A COUNT COUNTS. `UnitId` in the engine's vocabulary, and the reason a movement chart
 * cannot simply add its rows up: twelve reps, twelve calories and twelve metres are three
 * different amounts of work and only one of them is reps.
 */
export const MOVEMENT_UNITS = ["reps", "calories", "metres", "seconds"] as const;
export const movementUnitSchema = z.enum(MOVEMENT_UNITS);
export type MovementUnit = (typeof MOVEMENT_UNITS)[number];

/**
 * ONE SET, AS IT WAS ACTUALLY PERFORMED — `db/schemas/tables/session_log_lines.sql`.
 *
 * 🚨 THIS IS THE SHAPE THE WIRE REFUSED TO CARRY, AND THE REFUSAL WAS COSTING THE PRODUCT ITS
 * MOST VALUABLE DATA. `api/src/domains/logs/routes.ts` said so in its own words — "`LineReport`
 * … is the highest-signal correction a fitting job can get, and `createSessionLogSchema` has no
 * field for any of it" — and the strength screen said the same from the other end: the weight,
 * the reps and the feel of every set were local component state, lost on unmount, because there
 * was no endpoint to send them to. A movement's history, its volume and its best set are all
 * this shape, repeated.
 *
 * ⚠️ `loadKg` IS KILOS, ALWAYS, AND `null` IS NOT ZERO. Null means the movement carries no
 * load — a pull-up — and zero means an empty bar, which is a real prescription. The athlete's
 * own unit is a display choice made on the phone; storing pounds because that is what they
 * read would make every aggregate depend on which unit was selected on the day.
 *
 * ⚠️ `restSeconds` IS THE REST BEFORE THIS SET, NOT AFTER IT. Rest-after leaves the last set of
 * a block holding a rest that belongs to whatever came next, or to going home.
 */
export const sessionLogLineSchema = z.object({
  /** Which part of the session this set belonged to. A squat in the lift and a squat inside a
   *  metcon are the same movement doing two different jobs, and history keeps them apart. */
  block: blockIdSchema,
  /** The catalog slug — `back-squat`. The identity every movement chart groups on. */
  movement: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9-]+$/, "invalid"),
  /**
   * The name the card PRINTED on the day, carried rather than looked up.
   *
   * The engine publishes no catalog the app or the database can read — `engine_movements` is
   * an overlay holding only the rows a coach edited — so a slug resolves to a name in exactly
   * one place, inside the engine. Carrying it is also the more honest record: a rename three
   * months from now does not retroactively change what the athlete read.
   */
  movementName: z.string().min(1).max(120),
  unit: movementUnitSchema,
  /**
   * WHICH OF THE THREE KINDS OF WORK THIS IS — `weightlifting` · `monostructural` ·
   * `gymnastic`, the engine's own registry.
   *
   * 🔑 IT IS WHAT THE MOVEMENT LIST GROUPS ON, and grouping is the difference between a screen
   * an athlete can scan and a flat alphabet of forty names. Carried rather than derived: every
   * cheap proxy is wrong somewhere — "has a weight on it" files a weighted pull-up under
   * weightlifting, and a unit says nothing about a burpee. The engine decides it from a
   * declared test with a precedence order and puts the answer on every movement line.
   *
   * A PLAIN STRING AND NOT AN ENUM, matching how `modality` already travels on
   * `movementLineSchema`: the registry is rows in code, and the whole design of that file is
   * that a second sport means adding a row. An enum here would make a fourth one a wire break.
   *
   * Null on a line recorded before this field existed. A reader groups those under "Other"
   * rather than dropping them or guessing.
   */
  modality: z.string().min(1).max(40).nullable(),
  source: lineSourceSchema,
  /** 1-based, within this movement in this session. */
  setIndex: z.number().int().positive().max(200),
  /** Completed, in `unit`. Null where nobody counted — which is not the same as none. */
  reps: z.number().int().min(0).max(100_000).nullable(),
  /** Kilos on the bar. Null for an unloaded movement; zero is an empty bar. */
  loadKg: z.number().min(0).max(1000).nullable(),
  /** 1–5, exactly as tapped. See the ratings note above — nothing converts it. */
  feel: ratingSchema.nullable(),
  /** Where this set ended on the WHOLE-SESSION clock, never on the metcon's. */
  atSeconds: z.number().int().min(0).max(86_400).nullable(),
  /** How long they rested BEFORE it. Null on the first set, and on every derived line. */
  restSeconds: z.number().int().min(0).max(86_400).nullable(),
  /**
   * ═══ WHAT WAS ASKED FOR — the other half of every line, and the half this wire did not carry ═══
   *
   * 🚨 A SET WITHOUT ITS PRESCRIPTION IS UNREADABLE AFTER THE FACT (BUD-376). `5 reps @ 100` is a
   * completed 5×5 or three short of an 8-rep prescription, and `100, 100, 80, 70, 70` is either a
   * programmed back-off — correct programming, which `movements/repo.ts` already has to allow for
   * — or an athlete retreating because it was too heavy. Those two mean OPPOSITE things and every
   * progression rule reads performed against prescribed, so a line that carries only the performed
   * half cannot be evaluated by any of them.
   *
   * 🔑 IT IS WHAT THE CARD SAID WHEN THEY STARTED THE SET — post-swap, post-edit — and it is STORED,
   * never joined for later. The plan is mutable (a line swapped, a load edited, a session redrawn),
   * so a join computed afterwards answers "what does the plan say now", which is a different
   * question from "what did the card say when they lifted it", and only the second can say whether
   * they completed it. The same reasoning that carries `movementName` on every line.
   *
   * ⚠️ `null` MEANS NOT KNOWN — NEVER ZERO, AND NEVER A COMPLETION. A released build still writes
   * last month's shape, so every line recorded before these fields existed carries three nulls, and
   * a card that printed no number ("Deadlift", no scheme) is recorded as saying nothing rather than
   * as saying one. A reader that treated null as "prescribed nothing" would mark every old session
   * as a completed prescription. Null on every `derived` line too: those are back-computed from a
   * block total, and nobody was asked to do a specific set.
   */
  /** Kilos the card said for THIS set. Null where it said nothing, or nobody knows. */
  prescribedLoadKg: z.number().min(0).max(1000).nullable(),
  /** Reps the card said for THIS set, in `unit`. Null where it said nothing, or nobody knows. */
  prescribedReps: z.number().int().min(0).max(100_000).nullable(),
  /** How many sets the block asked for. Null where it said nothing, or nobody knows. */
  prescribedSets: z.number().int().positive().max(200).nullable(),
});

export type SessionLogLineDTO = z.infer<typeof sessionLogLineSchema>;

/**
 * WHEN ONE BLOCK HAPPENED, on the whole-session clock —
 * `db/schemas/tables/session_log_segments.sql`.
 *
 * 🔑 THIS IS WHAT LABELS EVERY OTHER TIMED THING. A heart-rate spike at minute 31 is a
 * different fact depending on whether minute 31 was the metcon or the cooldown, and without
 * these spans a whole-session trace can be drawn and not read. It is also what lets a screen
 * zoom a trace to the metcon alone, which is the view worth studying.
 *
 * `endSeconds` is null for a block the athlete opened and never finished. That is a thing that
 * happened, not a gap in the record.
 */
export const sessionLogSegmentSchema = z.object({
  block: blockIdSchema,
  startSeconds: z.number().int().min(0).max(86_400),
  endSeconds: z.number().int().min(0).max(86_400).nullable(),
});

export type SessionLogSegmentDTO = z.infer<typeof sessionLogSegmentSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   THE HEART-RATE TRACE — and the two numbers that make storing one honest
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * THE FIVE EFFORT ZONES, DECLARED ON THE WIRE BECAUSE BOTH SIDES NOW NEED THE SAME FIVE.
 *
 * They are ABSOLUTE beats per minute — Z1 under 120 · Z2 120–137 · Z3 138–154 · Z4 155–169 ·
 * Z5 170 and up — and not a percentage of anybody's maximum: this product knows no athlete's
 * age, resting rate or tested max, and deriving one from a birth year would be a number that
 * looks personalised and is not.
 *
 * ⚠️ THE BOUNDARIES ARE NOT HERE. This is the VOCABULARY; the thresholds and the hysteresis
 * that stops a screen strobing on a boundary live in `src/shared/heart-rate/heart-rate.model.ts`,
 * which is the one executable copy. A second copy on the wire would be a second answer to
 * "which zone is 138", and the two would disagree the first time either moved.
 */
export const HEART_RATE_ZONES = ["z1", "z2", "z3", "z4", "z5"] as const;
export const heartRateZoneSchema = z.enum(HEART_RATE_ZONES);
export type HeartRateZoneId = (typeof HEART_RATE_ZONES)[number];

/**
 * ONE MEASUREMENT — `[atSeconds, bpm]`, where `atSeconds` is the offset on the WHOLE-SESSION
 * clock: the same zero as a line's `atSeconds` and a segment's `startSeconds`.
 *
 * A PAIR AND NOT AN OBJECT, which is the one place on this wire where that trade is worth
 * making. An hour of training is about 3,600 of these, and `{"atSeconds":1234,"bpm":152}`
 * is four times the bytes of `[1234,152]` for the same two numbers — over a gym's wifi, on the
 * one request an athlete cannot be asked to retry.
 */
export const heartRateSampleSchema = z.tuple([
  z.number().int().min(0).max(86_400),
  z.number().int().min(1).max(300),
]);

export type HeartRateSampleDTO = z.infer<typeof heartRateSampleSchema>;

/**
 * HOW LONG WAS SPENT IN EACH ZONE, in seconds. Over the COVERED time only — see the trace.
 */
export const heartRateZoneSecondsSchema = z.object({
  z1: z.number().int().min(0).max(86_400),
  z2: z.number().int().min(0).max(86_400),
  z3: z.number().int().min(0).max(86_400),
  z4: z.number().int().min(0).max(86_400),
  z5: z.number().int().min(0).max(86_400),
});

export type HeartRateZoneSecondsDTO = z.infer<typeof heartRateZoneSecondsSchema>;

/**
 * A WHOLE SESSION'S HEART RATE — and this shape REVERSES A DELIBERATE DECISION.
 *
 * ═══ WHAT THE OLD DECISION WAS ═══
 *
 * `src/app/platform/heart-rate.ts` refused to write anything down, in its own words: "BLE has
 * no backfill, so a strap that drops at minute four leaves a hole nothing can fill — and a
 * lossy display feed recorded as if it were the record silently corrupts history."
 *
 * Every word of that is still true. What made recording unsafe was never the gaps; it was that
 * a stored trace with UNMARKED gaps reads as a complete record, so an average computed over
 * whichever minutes the strap happened to survive gets printed as "average heart rate" and
 * believed.
 *
 * ═══ WHAT MAKES IT SAFE, AND IT IS TWO FIELDS RATHER THAN A PROMISE ═══
 *
 * 🚨 `coveredSeconds` AND `sessionSeconds` ARE REQUIRED AND ARE NOT DERIVABLE AFTERWARDS.
 * Together they say what fraction of the session the strap was actually delivering, and every
 * surface that prints a number off this shape prints that fraction beside it ("connected for
 * 41 of 58 min"). A 12-minute average over a 58-minute session is then visibly a 12-minute
 * average, on the screen, rather than in a comment nobody reads.
 *
 * 🚨 GAPS TRAVEL AS GAPS. Samples carry their own offsets, so a 90-second dropout is 90
 * seconds with no sample in it. A reader MUST break the line where the samples break —
 * interpolating across a hole is the one transformation that makes a dropout indistinguishable
 * from a plateau, which is exactly the corruption the original decision was protecting against.
 */
export const heartRateTraceSchema = z.object({
  /**
   * Ascending, at most one per second. The ceiling is six hours at 1 Hz — far past any
   * session, and a bound so one request cannot become the outage.
   */
  samples: z.array(heartRateSampleSchema).max(21_600),
  /** Seconds of the session the strap was actually delivering. The honesty numerator. */
  coveredSeconds: z.number().int().min(0).max(86_400),
  /** How long the session ran. The denominator, so a reader needs no second lookup. */
  sessionSeconds: z.number().int().min(0).max(86_400),
  /** Over the covered seconds only. Null when the strap connected and delivered nothing. */
  averageBpm: z.number().int().min(1).max(300).nullable(),
  peakBpm: z.number().int().min(1).max(300).nullable(),
  zones: heartRateZoneSecondsSchema,
});

export type HeartRateTraceDTO = z.infer<typeof heartRateTraceSchema>;

/**
 * ONE LOGGED SESSION.
 *
 * `capHit` IS THREE-STATE AND THE THIRD STATE IS NULL. True: the clock stopped them. False:
 * they finished the work. Null: nobody asked. A boolean with a default would file "we never
 * asked" as "they finished", which is the exact bias the engine's censoring model exists to
 * prevent — capped pieces are precisely the ones that ran long, so mis-filing them deletes
 * the slow tail from the evidence and then re-derives a work rate from what is left.
 */
export const sessionLogSchema = z.object({
  id: z.uuid(),
  title: z.string().min(1).max(120),
  /**
   * The day they trained — a date, not a timestamp. The engine's request date is `YYYY-MM-DD`
   * in the athlete's own calendar and every ageing rule measures against it.
   */
  performedOn: z.iso.date(),
  format: metconFormatIdSchema,
  load: logLoadSchema,
  outcome: completionOutcomeSchema,
  /**
   * THE METCON'S TIME, and only the metcon's — the number the conditioning piece is scored on.
   * Null where nobody recorded one.
   */
  elapsedSeconds: z.number().int().positive().nullable(),
  /**
   * HOW LONG THE WHOLE SESSION TOOK, which is a different number and used to be unanswerable.
   *
   * The clock starts when the athlete opens the first block and stops when the debrief is
   * written, so it spans the warm-up, the lift, the metcon and the cooldown. `elapsedSeconds`
   * above spans one of those four. Null for a session logged by hand and for every row written
   * before the column existed — zero would be a session that took no time.
   */
  sessionSeconds: z.number().int().positive().nullable(),
  /** Null where the format prescribes rounds rather than scoring them. */
  rounds: z.number().int().min(0).nullable(),
  /**
   * HOW FAR INTO THE UNFINISHED ROUND THEY GOT, in reps across every movement of it — the
   * "+ 12" of "5 rounds + 12".
   *
   * 🚨 WHOLE ROUNDS ALONE THROW AWAY UP TO A ROUND OF WORK. An athlete stopped by the clock
   * most of the way through round six banks five, and reads a score for a worse session than
   * they had. This is the half that was missing, and it is also how a round-scored piece is
   * actually scored everywhere outside this app.
   *
   * ⚠️ REPS, WHATEVER THE MOVEMENTS MEASURE. A round of twelve wall balls and a 200 m run
   * scores as its rep count: what is compared is progress through ONE round of ONE workout, not
   * work in the abstract. The per-movement detail is not lost — it is added into that
   * movement's own line, where volume and history read it.
   *
   * Null where the format is not scored by rounds. Zero is a real and different answer: the
   * athlete stopped exactly on a boundary.
   */
  partialReps: z.number().int().min(0).nullable(),
  capHit: z.boolean().nullable(),
  performance: ratingSchema.nullable(),
  fatigue: ratingSchema.nullable(),
  feeling: ratingSchema.nullable(),
  /** The session as a whole. See the ratings note above — 1–5, passed straight through. */
  rpe: ratingSchema.nullable(),
  /**
   * The offer this session came from, where it came from one. DERIVED from the request rather
   * than minted, so it is the join key between what was offered and what was done. Null for a
   * session the athlete logged by hand — which is why it is a string and not a `z.uuid()`.
   */
  requestId: z.string().nullable(),
  /**
   * ═══ WHAT THE SESSION WAS, RECOVERABLY ═══
   *
   * 🚨 EVERY OTHER FIELD ON THIS SHAPE IS A SCORE. A time, a round count, three ratings — not
   * one movement, not one load, not one rep scheme. So a history row could say that Tuesday
   * happened and never what Tuesday WAS, and "do that one again" could not be built at all.
   *
   * The workout is not copied here, and it must not be: an offer is a pure function of a SEED
   * and the athlete's state, so the seed IS the session. A reader redraws from it — the same
   * read the session screen already makes — rather than this shape growing a second, frozen
   * copy of a session that would then disagree with the engine's own.
   *
   * ⚠️ A REDRAW IS NOT A PHOTOGRAPH. The draw reads maxes, ladders and recent history, all of
   * which move, so the same seed a month later can price the same movements differently. The
   * app redraws onto a screen that shows the whole session before anything starts, which is
   * what makes that honest rather than a swap nobody notices.
   *
   * Null for a session logged by hand, and for every row written before this field existed.
   */
  seed: z.number().int().nonnegative().nullable(),
  /**
   * WHICH OF THE THREE COLUMNS WAS STARTED — and it cannot be recovered from `load`, which is
   * derived from it (push → rx, everything else → scaled). `mid` and `easy` are both `scaled`,
   * and redrawing the wrong one is a different workout at a different intensity.
   */
  gear: gearIdSchema.nullable(),
  /**
   * WHY THEY STOPPED, AND WHICH KIND OF STOPPING IT WAS (BUD-337). Both null on every session
   * that was not cancelled, and `cancelReason` is null as well on one that was cancelled with
   * no reason given — the design's `Skip · just cancel`, which is a real answer and not a gap.
   *
   * ⚠️ `cancelStage` IS NON-NULL EXACTLY WHEN `outcome` IS `abandoned`, for rows written from
   * BUD-337 onwards. The table's own check enforces the other direction — neither field may
   * carry a value on a row that is not abandoned — so a `completed` session can never claim a
   * reason it was never asked for.
   */
  cancelReason: cancelReasonSchema.nullable(),
  cancelStage: cancelStageSchema.nullable(),
  /**
   * DID THE ATHLETE CHANGE A LINE OF IT (BUD-347) — a movement substituted, or a weight taken
   * off the published ladder.
   *
   * 🚨 IT IS WHY A HISTORY ROW MAY NOT BE COMPARED TO A BENCHMARK. Design frame `6a` tells the
   * athlete so before they choose — "swapping marks the session modified — it will not compare
   * to your benchmark" — and this is the field that keeps the promise. A Fran with the thrusters
   * swapped is not a Fran, and a personal best drawn from one is a number that will never be
   * beaten honestly.
   *
   * FALSE FOR EVERY SESSION LOGGED BY HAND and for every row written before the column existed:
   * a session nobody changed is exactly what was drawn, which is the ordinary case.
   */
  modified: z.boolean(),
  splits: z.array(sessionLogSplitSchema),
  /**
   * EVERY SET, AS IT WAS PERFORMED — the strength work the athlete tapped in, and the metcon
   * movements derived from the rounds they banked. Empty for a session trained before this
   * wire carried a set, which is every session logged until now.
   */
  lines: z.array(sessionLogLineSchema),
  /** When each block happened on the session clock. What labels the trace below. */
  segments: z.array(sessionLogSegmentSchema),
  /**
   * The strap's reading across the session, or null where none was worn or none was recorded.
   * Read the shape's own header before drawing it: the gaps are real and must stay visible.
   */
  heartRate: heartRateTraceSchema.nullable(),
  /**
   * Whether THIS caller has favourited it. Derived from a join row rather than stored on the
   * log, so it is on the read view and appears in no write payload: favouriting is its own
   * action against `session_favourites`, and a field a client could PUT would be a second way
   * to write a row that has its own endpoint.
   */
  isFavourite: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type SessionLogDTO = z.infer<typeof sessionLogSchema>;

/**
 * THE HISTORY ROW — what a list screen renders, without the splits.
 *
 * A SECOND, GENUINELY DIFFERENT SHAPE rather than a description of one. Splits are the
 * expensive part of a log and a history list draws none of them: a fifty-round EMOM carries
 * fifty rows that a row two lines tall cannot show. This is the contract being the field
 * whitelist rather than a convention somebody remembers.
 */
export const sessionLogSummarySchema = sessionLogSchema.omit({
  splits: true,
  /**
   * ⚠️ THE THREE NEW COLLECTIONS ARE OMITTED FOR THE SAME REASON THE SPLITS ALWAYS WERE, and
   * the argument got STRONGER rather than weaker. A history row two lines tall draws none of
   * them, and a heart-rate trace is thousands of samples: a page of twenty sessions carrying
   * one each would be megabytes over a gym's wifi to render twenty dates and twenty times. The
   * screen that draws them asks for one log.
   */
  lines: true,
  segments: true,
  heartRate: true,
});

export type SessionLogSummaryDTO = z.infer<typeof sessionLogSummarySchema>;

/**
 * THE WRITABLE FIELDS, WITH NO DEFAULTS ON THEM. Read the note on `updateSessionLogSchema`
 * before moving a `.default()` up here — it is the reason this object exists separately.
 */
const sessionLogWritable = z.object({
  title: z.string().min(1, "required").max(120),
  performedOn: z.iso.date(),
  format: metconFormatIdSchema,
  load: logLoadSchema,
  outcome: completionOutcomeSchema,
  elapsedSeconds: z.number().int().positive().max(86_400).nullable().optional(),
  /** The whole-session clock. See the read shape above for why it is not `elapsedSeconds`. */
  sessionSeconds: z.number().int().positive().max(86_400).nullable().optional(),
  rounds: z.number().int().min(0).max(10_000).nullable().optional(),
  /** See the read shape above. The ceiling is a round's worth, not a session's. */
  partialReps: z.number().int().min(0).max(10_000).nullable().optional(),
  capHit: z.boolean().nullable().optional(),
  performance: ratingSchema.nullable().optional(),
  fatigue: ratingSchema.nullable().optional(),
  feeling: ratingSchema.nullable().optional(),
  rpe: ratingSchema.nullable().optional(),
  requestId: z.string().max(200).nullable().optional(),
  /**
   * Sent whole, never one at a time. The table's `unique (log_id, round)` makes a duplicate
   * round a conflict rather than a silent second row, and a client that PATCHes one split
   * into an existing set has no way to express "these are all of them".
   */
  splits: z.array(sessionLogSplitSchema).max(500),
  /**
   * EVERY SET, SENT WHOLE — the same rule as the splits above, and the same reason: the stored
   * set is REPLACED rather than merged, because a client sending one row at a time has no way
   * to say "these are all of them" and the table's `unique` would turn a correction into a
   * conflict.
   *
   * The ceiling is generous on purpose. A strength day is a dozen rows; a long metcon with six
   * movements derived across it is a few dozen. 500 is the bound that stops one request
   * becoming the outage, not a number anybody should reach.
   */
  lines: z.array(sessionLogLineSchema).max(500),
  /** One per block the athlete actually ran. Six blocks exist, so this is never long. */
  segments: z.array(sessionLogSegmentSchema).max(20),
  /**
   * The trace, or null. Null is the ordinary case — most sessions are trained without a strap
   * — and is emphatically NOT the same as a trace with no samples in it, which means the strap
   * was connected and delivered nothing.
   */
  heartRate: heartRateTraceSchema.nullable(),
});

/**
 * Creating carries the defaults: the column's own `default 'completed'`, and empty collections.
 *
 * 🚨 EVERY DEFAULT LIVES HERE AND NEVER ON `sessionLogWritable`, and the note on
 * `updateSessionLogSchema` below is why — a `.default()` up there survives `.partial()` and
 * turns renaming a log into an erasure of everything the session recorded.
 */
export const createSessionLogSchema = sessionLogWritable.extend({
  outcome: completionOutcomeSchema.default("completed"),
  splits: z.array(sessionLogSplitSchema).max(500).default([]),
  lines: z.array(sessionLogLineSchema).max(500).default([]),
  segments: z.array(sessionLogSegmentSchema).max(20).default([]),
  heartRate: heartRateTraceSchema.nullable().default(null),
});

export type CreateSessionLogPayload = z.infer<typeof createSessionLogSchema>;

/**
 * ⚠️ BUILT FROM THE BARE SHAPE, NOT FROM `createSessionLogSchema.partial()`, AND HERE THAT
 * RULE HAS TEETH.
 *
 * `.partial()` makes a key optional; it does NOT remove a `.default()` underneath it. So
 * `createSessionLogSchema.partial().parse({ title: "New title" })` returns
 * `{ title, outcome: "completed", splits: [], lines: [], segments: [], heartRate: null }` —
 * renaming a log would quietly reset an `abandoned` session to `completed` AND WIPE EVERY
 * RECORDED SPLIT, EVERY SET, EVERY BLOCK SPAN AND THE WHOLE HEART-RATE TRACE, because each
 * resolves to its default rather than being absent. The trap got four times more expensive
 * when this wire learned to carry what actually happened. The one field the caller sent comes
 * back as three, and the two they did not send destroy data.
 *
 * So a partial update is derived from the shape that never had defaults, and
 * `shared/contracts/__tests__/write-payloads.test.ts` holds it there. Types cannot see this:
 * both spellings infer the same optional fields, and only a parse tells them apart.
 */
export const updateSessionLogSchema = sessionLogWritable.partial();

export type UpdateSessionLogPayload = z.infer<typeof updateSessionLogSchema>;

/**
 * THE PAGE SIZE FOR HISTORY, and the ceiling on one.
 *
 * 20 is around two phone screens of a list that loads by being scrolled. 100 is the bound
 * that keeps one request from becoming the outage — at this contract's own worst case a
 * summary row is small, but a `max` exists so the number cannot be chosen by the caller.
 * Keyset, never OFFSET: history is scrolled while today's session is being written into it.
 */
export const listSessionLogsSchema = z.object({
  cursor: z.string().nullable().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  /**
   * Narrow to what the athlete kept. A separate flag, not a separate endpoint's worth of shape.
   *
   * NOT `z.coerce.boolean()`, WHICH IS A TRAP ON A QUERY STRING. Coercion is
   * JavaScript's own truthiness, so `?favouritesOnly=false` arrives as the non-empty string
   * "false" and coerces to TRUE — the filter turns itself on when a caller explicitly turns
   * it off, and nothing anywhere errors. This union accepts a real boolean from a JSON body
   * and exactly the two spellings from a query string, and REFUSES anything else rather than
   * guessing at it.
   */
  favouritesOnly: z
    .union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")])
    .default(false),
});

export type ListSessionLogsQuery = z.infer<typeof listSessionLogsSchema>;

export const sessionLogListSchema = z.object({
  items: z.array(sessionLogSummarySchema),
  nextCursor: z.string().nullable(),
});

export type SessionLogListDTO = z.infer<typeof sessionLogListSchema>;

/** The id in `/v1/logs/:id`. */
export const sessionLogIdSchema = z.object({ id: z.uuid() });

export type SessionLogIdParam = z.infer<typeof sessionLogIdSchema>;

/**
 * WHAT FAVOURITING ANSWERS. A join row and nothing else — no rating, no note, no ordering,
 * because anything more is a fact about the session and belongs on the log, where one row
 * holds it once. Favouriting twice is the same as favouriting once.
 */
export const favouriteStateSchema = z.object({ isFavourite: z.boolean() });

export type FavouriteStateDTO = z.infer<typeof favouriteStateSchema>;


/* ══════════════════════════════════════════════════════════════════════════════════════
   TRAINING ACTIVITY, ONE ROW PER DAY — what a year-of-days grid is drawn from
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * ONE DAY AN ATHLETE TRAINED.
 *
 * 🚨 THE INTENSITY IS `sets` AND NOT A VOLUME, and that is the one decision in this shape.
 *
 * Volume in kilos is the obvious measure and it is wrong here: a day of pull-ups, running and
 * burpees carries no load at all, so it would total zero and the grid would draw a blank cell —
 * telling an athlete who trained hard that they did not train. Across ALL movements the measure
 * has to be unit-agnostic, and a set is the one unit every kind of work produces.
 *
 * `seconds` travels beside it because it is the other honest answer to "how big was that day",
 * and a caller may prefer it. Null on a session trained before the clock existed.
 */
export const logActivityDaySchema = z.object({
  /** `YYYY-MM-DD` in the athlete's own calendar. */
  day: z.iso.date(),
  /** Sessions trained that day. Two-a-days are real and are counted as two. */
  sessions: z.number().int().min(0),
  /** Every recorded set, measured and derived alike — this is a measure of ACTIVITY, not proof. */
  sets: z.number().int().min(0),
  /** Total time on the floor across the day's sessions, where it was measured. */
  seconds: z.number().int().min(0).nullable(),
});

export type LogActivityDayDTO = z.infer<typeof logActivityDaySchema>;

/**
 * A YEAR OF DAYS, AND ONLY THE ONES THAT HAPPENED.
 *
 * ⚠️ A DAY WITH NO TRAINING IS ABSENT RATHER THAN SENT AS A ZERO. Three hundred and sixty-five
 * rows to describe about a hundred and fifty training days is more than twice the payload for no
 * added fact — the grid fills its own gaps, because a grid is exactly the shape that knows which
 * days it is missing.
 */
export const logActivitySchema = z.object({
  days: z.array(logActivityDaySchema).max(1500),
});

export type LogActivityDTO = z.infer<typeof logActivitySchema>;

/** How far back the activity grid reads. One window, because the grid is always a year. */
export const logActivityQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(1200).default(371),
});

export type LogActivityQuery = z.infer<typeof logActivityQuerySchema>;
